'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { BagSVG, GridIconSVG, SearchSVG } from '@/src/ui/AllSVG'
import { getCartPath, getCategoriesPath, getHomePath } from '@/src/routes/routes'

type MobileBottomNavProps = {
    menuOpen: boolean
    searchOpen: boolean
    cartCount: number
    isAuthenticated: boolean
    onMenuToggle: () => void
    onSearchOpen: () => void
    onSignIn: () => void
}

const HomeIconSVG = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" className={className}>
        <path
            d="M9.02 2.84004L3.63 7.04004C2.73 7.74004 2 9.23004 2 10.36V17.77C2 20.09 3.89 21.99 6.21 21.99H17.79C20.11 21.99 22 20.09 22 17.78V10.5C22 9.29004 21.19 7.74004 20.2 7.05004L14.02 2.72004C12.62 1.74004 10.37 1.79004 9.02 2.84004Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M12 17.99V14.99" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const MenuIconSVG = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" className={className}>
        <path d="M3 7H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M3 12H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M3 17H21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
)

function navItemClass(active: boolean) {
    return `flex flex-1 flex-col items-center justify-center gap-1 py-2.5 min-h-14 text-[10px] uppercase tracking-wider font-medium transition-colors cursor-pointer ${
        active ? 'text-heading-color' : 'text-body-color/55 hover:text-body-color'
    }`
}

export default function MobileBottomNav({
    menuOpen,
    searchOpen,
    cartCount,
    isAuthenticated,
    onMenuToggle,
    onSearchOpen,
    onSignIn,
}: MobileBottomNavProps) {
    const pathname = usePathname()
    const isDashboard = pathname.startsWith('/dashboard')

    if (isDashboard) return null

    const isHome = pathname === getHomePath()
    const isShop = pathname === getCategoriesPath() || pathname.startsWith(`${getCategoriesPath()}?`)
    const isCart = pathname === getCartPath()

    return (
        <nav
            className="lg:hidden fixed bottom-0 inset-x-0 z-50 border-t border-muted-color bg-white/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)]"
            aria-label="Mobile navigation"
        >
            <div className="flex items-stretch">
                <Link href={getHomePath()} className={navItemClass(isHome)} aria-current={isHome ? 'page' : undefined}>
                    <HomeIconSVG />
                    <span>Home</span>
                </Link>

                <Link href={getCategoriesPath()} className={navItemClass(isShop)} aria-current={isShop ? 'page' : undefined}>
                    <GridIconSVG />
                    <span>Shop</span>
                </Link>

                <button
                    type="button"
                    onClick={onMenuToggle}
                    className={navItemClass(menuOpen)}
                    aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={menuOpen}
                >
                    <MenuIconSVG />
                    <span>Menu</span>
                </button>

                <button
                    type="button"
                    onClick={onSearchOpen}
                    className={navItemClass(searchOpen)}
                    aria-label="Search"
                    aria-expanded={searchOpen}
                >
                    <SearchSVG />
                    <span>Search</span>
                </button>

                {isAuthenticated ? (
                    <Link
                        href={getCartPath()}
                        className={`${navItemClass(isCart)} relative`}
                        aria-current={isCart ? 'page' : undefined}
                        aria-label={cartCount > 0 ? `Bag, ${cartCount} items` : 'Bag'}
                    >
                        <span className="relative">
                            <BagSVG />
                            {cartCount > 0 && (
                                <span className="absolute -top-2 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full bg-heading-color text-white text-[9px] font-semibold leading-none">
                                    {cartCount > 99 ? '99+' : cartCount}
                                </span>
                            )}
                        </span>
                        <span>Bag</span>
                    </Link>
                ) : (
                    <button type="button" onClick={onSignIn} className={navItemClass(false)} aria-label="Bag">
                        <BagSVG />
                        <span>Bag</span>
                    </button>
                )}
            </div>
        </nav>
    )
}
